import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useAuth0 } from '@auth0/auth0-react'
import { useSupabase } from '@/lib/supabase-context'
import { useOrganizacion } from '@/core/providers/OrganizacionProvider'
import { toast } from 'sonner'
import { usePermissions } from '@/hooks/usePermissions'
import { assertPermiso } from '@/lib/permisos-guard'
import {
  configuracionService,
  type ConfiguracionEstudio,
  type PromoInstagramConfig,
  type PreferenciasUsuario,
} from '@/services/configuracionService'

// Defaults para preferencias
const PREFERENCIAS_DEFAULT: Omit<PreferenciasUsuario, 'id' | 'user_id'> = {
  tema: 'system',
  email_nuevo_cliente: true,
  email_nuevo_movimiento: false,
  email_recordatorio_vencimiento: true,
  email_recordatorio_lote_deudas: true,
}

// =====================================
// Query Keys
// =====================================

export const configuracionKeys = {
  all: ['configuracion'] as const,
  estudio: (orgId?: string | null) => [...configuracionKeys.all, 'estudio', orgId] as const,
  preferencias: (userId?: string | null) => [...configuracionKeys.all, 'preferencias', userId] as const,
  promoInstagram: (orgId?: string | null) => [...configuracionKeys.all, 'promo-instagram', orgId] as const,
}

// =====================================
// Hooks para Configuración del Estudio
// =====================================

export function useConfiguracionEstudio() {
  const { supabase, isLoading: supabaseLoading } = useSupabase()
  const { organizacionId } = useOrganizacion()

  return useQuery({
    queryKey: configuracionKeys.estudio(organizacionId),
    queryFn: () => configuracionService.obtenerConfiguracion(supabase!, organizacionId!),
    staleTime: 60 * 60 * 1000,
    enabled: !!supabase && !supabaseLoading && !!organizacionId,
  })
}

export function useGuardarFirmaEmail() {
  const queryClient = useQueryClient()
  const { supabase } = useSupabase()
  const { organizacionId } = useOrganizacion()

  return useMutation({
    mutationFn: (firma: {
      firma_email_html: string | null
      firma_email_imagen_url: string | null
      firma_email_sello_url: string | null
      logo_url: string | null
      instagram_url: string | null
      whatsapp: string | null
    }) => {
      if (!supabase) throw new Error('Supabase no disponible')
      if (!organizacionId) throw new Error('Organización no disponible')
      return configuracionService.actualizarFirmaEmail(supabase, organizacionId, firma)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: configuracionKeys.estudio(organizacionId) })
      toast.success('Firma de emails guardada correctamente')
    },
    onError: (error) => {
      console.error('Error guardando firma email:', error)
      toast.error('Error al guardar la firma')
    },
  })
}

export function useGuardarConfiguracionEstudio() {
  const queryClient = useQueryClient()
  const { supabase } = useSupabase()
  const { organizacionId } = useOrganizacion()

  return useMutation({
    mutationFn: (config: ConfiguracionEstudio) => {
      if (!supabase) throw new Error('Supabase no disponible')
      if (!organizacionId) throw new Error('Organización no disponible')
      return configuracionService.guardarConfiguracion(supabase, organizacionId, config)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: configuracionKeys.estudio(organizacionId) })
      toast.success('Datos del estudio guardados correctamente')
    },
    onError: (error) => {
      console.error('Error guardando configuracion estudio:', error)
      toast.error('Error al guardar los datos del estudio')
    },
  })
}

// =====================================
// Instagram en los mails a clientes (plan-promo-instagram-mails)
// =====================================

export function usePromoInstagramConfig() {
  const { supabase, isLoading: supabaseLoading } = useSupabase()
  const { organizacionId } = useOrganizacion()

  return useQuery({
    queryKey: configuracionKeys.promoInstagram(organizacionId),
    queryFn: () => configuracionService.obtenerPromoInstagram(supabase!, organizacionId!),
    enabled: !!supabase && !supabaseLoading && !!organizacionId,
  })
}

export function useGuardarPromoInstagram() {
  const queryClient = useQueryClient()
  const { supabase } = useSupabase()
  const { organizacionId } = useOrganizacion()
  const { puede } = usePermissions()

  return useMutation({
    mutationFn: (config: PromoInstagramConfig) => {
      assertPermiso(puede, 'editarConfiguracion')
      if (!supabase) throw new Error('Supabase no disponible')
      if (!organizacionId) throw new Error('Organización no disponible')
      return configuracionService.guardarPromoInstagram(supabase, organizacionId, config)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: configuracionKeys.promoInstagram(organizacionId) })
      toast.success('Instagram en los mails guardado')
    },
    onError: (error) => {
      console.error('Error guardando Instagram en los mails:', error)
      toast.error('No se pudo guardar Instagram en los mails')
    },
  })
}

// =====================================
// Hooks para Preferencias de Usuario
// =====================================

export function usePreferenciasUsuario() {
  const { user } = useAuth0()
  const { supabase, isLoading: supabaseLoading } = useSupabase()

  return useQuery({
    queryKey: configuracionKeys.preferencias(user?.sub),
    queryFn: async () => {
      if (!user?.sub || !supabase) return null

      const prefs = await configuracionService.obtenerPreferencias(supabase, user.sub)

      if (!prefs) {
        const localPrefs = migrateFromLocalStorage()
        if (localPrefs) {
          await configuracionService.guardarPreferencias(supabase, {
            user_id: user.sub,
            ...localPrefs,
          })
          localStorage.removeItem('preferenciasNotificaciones')
          return { user_id: user.sub, ...localPrefs } as PreferenciasUsuario
        }
        return { user_id: user.sub, ...PREFERENCIAS_DEFAULT } as PreferenciasUsuario
      }

      return prefs
    },
    enabled: !!supabase && !supabaseLoading && !!user?.sub,
    staleTime: 5 * 60 * 1000,
  })
}

export function useGuardarPreferenciasUsuario() {
  const queryClient = useQueryClient()
  const { user } = useAuth0()
  const { supabase } = useSupabase()

  return useMutation({
    mutationFn: (prefs: Omit<PreferenciasUsuario, 'id' | 'user_id'>) => {
      if (!user?.sub) throw new Error('Usuario no autenticado')
      if (!supabase) throw new Error('Supabase no disponible')
      return configuracionService.guardarPreferencias(supabase, {
        user_id: user.sub,
        ...prefs,
      })
    },
    onSuccess: () => {
      // FIX: include user?.sub to match the query key
      queryClient.invalidateQueries({ queryKey: configuracionKeys.preferencias(user?.sub) })
      toast.success('Preferencias guardadas correctamente')
    },
    onError: (error) => {
      console.error('Error guardando preferencias:', error)
      toast.error('Error al guardar las preferencias')
    },
  })
}

function migrateFromLocalStorage(): Omit<PreferenciasUsuario, 'id' | 'user_id'> | null {
  try {
    const stored = localStorage.getItem('preferenciasNotificaciones')
    if (!stored) return null

    const parsed = JSON.parse(stored)
    return {
      tema: 'system',
      email_nuevo_cliente: parsed.emailNuevoCliente ?? PREFERENCIAS_DEFAULT.email_nuevo_cliente,
      email_nuevo_movimiento: parsed.emailNuevoMovimiento ?? PREFERENCIAS_DEFAULT.email_nuevo_movimiento,
      email_recordatorio_vencimiento: parsed.emailRecordatorioVencimiento ?? PREFERENCIAS_DEFAULT.email_recordatorio_vencimiento,
      email_recordatorio_lote_deudas: parsed.emailRecordatorioLoteDeudas ?? PREFERENCIAS_DEFAULT.email_recordatorio_lote_deudas,
    }
  } catch {
    return null
  }
}


// =====================================
// Hook helper para verificar notificaciones
// =====================================

type NotificacionKey = 'email_nuevo_cliente' | 'email_nuevo_movimiento' | 'email_recordatorio_vencimiento'

export function useDebeEnviarNotificacion() {
  const { data: prefs } = usePreferenciasUsuario()

  return (tipo: NotificacionKey): boolean => {
    if (!prefs) {
      return PREFERENCIAS_DEFAULT[tipo]
    }
    return prefs[tipo] ?? false
  }
}
